import { useServices } from '@/app/providers';
import { AsyncSearchableSelect, Loading, SearchableSelect } from '@/core/ui/components';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import { useDateFormatter } from '@/shared/hooks';
import type { Unit } from '@/shared/types';
import type { AccountStatementUnit } from '@yawi/types';
import { ArrowLeft, Building2, Calendar } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AccountStatementMovements, AccountStatementSummary, ReportExportButton } from '../components';
import { useAccountStatement } from '../hooks';

interface AccountStatementPageProps {
  onBack: () => void;
}

// Helper to get display name for a unit
const getUnitDisplayName = (unit: Unit | AccountStatementUnit): string => {
  const name = unit.name || '';
  const tower = unit.tower ? ` - Torre ${unit.tower}` : '';
  const floor = 'floor' in unit && unit.floor ? `, Piso ${unit.floor}` : '';
  return `${name}${tower}${floor}`.trim() || 'Unidad sin nombre';
};

export const AccountStatementPage = ({ onBack }: AccountStatementPageProps) => {
  const { reportRepo, unitRepo, currentUser } = useServices();
  const { formatDateTime } = useDateFormatter();
  const ability = useAbility();
  const isAdmin = ability.can('manage', 'all') || isSectionAdmin(currentUser);

  const [myUnits, setMyUnits] = useState<AccountStatementUnit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const initialLoadDone = useRef(false);

  const { statement, loading, exporting, params, setParams, refetch, exportStatement } = useAccountStatement({
    autoLoad: false,
  });

  // Load user's units and set initial unitId (only for non-admins)
  useEffect(() => {
    if (isAdmin) {
      // Admins don't need to load their own units - they search all units
      setLoadingUnits(false);
      initialLoadDone.current = true;
      return;
    }

    setLoadingUnits(true);
    reportRepo.getMyUnits()
      .then((units) => {
        setMyUnits(units);
        // If user has units, select the first one by default
        if (units.length > 0) {
          setParams({ unitId: units[0].id });
        }
        initialLoadDone.current = true;
      })
      .catch(console.error)
      .finally(() => setLoadingUnits(false));
  }, [reportRepo, setParams, isAdmin]);

  // Fetch statement once units are loaded and params are set
  useEffect(() => {
    if (initialLoadDone.current && !loadingUnits && params.unitId) {
      refetch();
    }
  }, [params, loadingUnits, refetch]);

  const handleUnitChange = (unitId: string | null | undefined) => {
    setParams(prev => ({ ...prev, unitId: unitId || undefined }));
  };

  const handleDateChange = (field: 'fromDate' | 'toDate', value: string) => {
    setParams(prev => ({ ...prev, [field]: value || undefined }));
  };

  // For admins: async load options from all units in the system
  const loadUnitOptions = useCallback(async (search: string) => {
    const result = await unitRepo.getUnits({ search, limit: 20 });
    return result.data.map((unit: Unit) => ({
      value: unit.id,
      label: getUnitDisplayName(unit),
    }));
  }, [unitRepo]);

  // For non-admins: convert their units to static options
  const userUnitOptions = myUnits.map((unit) => ({
    value: unit.id,
    label: getUnitDisplayName(unit),
  }));

  const hasMultipleUnits = myUnits.length > 1;
  const showUnitSelector = isAdmin || hasMultipleUnits;

  if (loadingUnits) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <h2 className="text-2xl font-bold text-slate-800">Estado de Cuenta</h2>
        </div>
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Estado de Cuenta</h2>
            <p className="text-slate-500 text-sm">
              {isAdmin
                ? 'Busca y selecciona una unidad para ver su estado de cuenta'
                : hasMultipleUnits
                  ? 'Selecciona una unidad para ver su estado de cuenta'
                  : 'Tu estado de cuenta personal'}
            </p>
          </div>
        </div>

        <ReportExportButton onExport={exportStatement} loading={exporting} disabled={loading || !statement} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-4">
          {showUnitSelector && (
            <div className="flex-1 min-w-[200px]">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                <Building2 size={16} />
                Unidad
              </label>
              {isAdmin ? (
                <AsyncSearchableSelect
                  loadOptions={loadUnitOptions}
                  value={params.unitId || null}
                  onChange={(val) => handleUnitChange(val)}
                  placeholder="Buscar unidad..."
                  defaultOptions
                  cacheOptions
                  isClearable
                  noOptionsMessage="No se encontraron unidades"
                  loadingMessage="Buscando unidades..."
                />
              ) : (
                <SearchableSelect
                  options={userUnitOptions}
                  value={params.unitId || null}
                  onChange={(val) => handleUnitChange(val)}
                  placeholder="Seleccionar unidad..."
                  isSearchable
                />
              )}
            </div>
          )}

          <div className="flex-1 min-w-[150px]">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} />
              Desde
            </label>
            <input
              type="date"
              value={params.fromDate || ''}
              onChange={(e) => handleDateChange('fromDate', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex-1 min-w-[150px]">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} />
              Hasta
            </label>
            <input
              type="date"
              value={params.toDate || ''}
              onChange={(e) => handleDateChange('toDate', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {!params.unitId ? (
        <div className="text-center py-12 text-slate-500">
          Selecciona una unidad para ver su estado de cuenta
        </div>
      ) : loading ? (
        <Loading />
      ) : statement ? (
        <>
          {/* Unit/Member Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex flex-wrap gap-6">
              <div>
                <div className="text-sm text-slate-500">Unidad</div>
                <div className="font-semibold text-slate-800">
                  {statement.unit.name}
                  {statement.unit.tower && ` - Torre ${statement.unit.tower}`}
                  {statement.unit.floor && `, Piso ${statement.unit.floor}`}
                </div>
              </div>
              {/* <div>
                <div className="text-sm text-slate-500">Propietario</div>
                <div className="font-semibold text-slate-800">{statement.member.name}</div>
                {statement.member.email && (
                  <div className="text-sm text-slate-400">{statement.member.email}</div>
                )}
              </div> */}
              <div className="ml-auto text-right">
                <div className="text-sm text-slate-500">Generado el</div>
                <div className="text-sm text-slate-600">
                  {formatDateTime(statement.generatedAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <AccountStatementSummary summary={statement.summary} />

          {/* Movements */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Movimientos</h3>
            </div>
            <AccountStatementMovements movements={statement.movements} />
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-slate-500">
          No se pudo cargar el estado de cuenta
        </div>
      )}
    </div>
  );
};
